/**
 * Tarjeta de producto de las grillas.
 *
 * Es un `<article>` porque cada tarjeta se entiende sola fuera de su lista. Todo
 * el bloque navegable es un `<a>` real hacia la ficha —la foto y el nombre
 * apuntan al mismo destino, con el nombre del producto como texto del enlace—,
 * que es lo que un robot puede seguir y lo que permite abrirlo en otra pestana.
 */
import { Link } from 'react-router-dom';

import type { ProductoResumen } from '@elcarpintero/compartido';

import { urlAbsoluta } from '../../seo/urls';
import { porcentajeDescuento } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { whatsappProducto } from '../../utilidades/whatsapp';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { BotonAgregar } from '../carro/BotonAgregar';
import { Icono } from '../comunes/Icono';
import { EtiquetaPaquete } from '../comunes/EtiquetaPaquete';
import { EtiquetaStock } from '../comunes/EtiquetaStock';
import { FotoProducto } from '../comunes/Imagen';
import { Precio } from '../comunes/Precio';

/**
 * Ancho de la foto en la grilla (`tarjetas.css`): columnas de 196 px o más en
 * escritorio, dos por fila en el teléfono. La primera fila carga sin diferir y
 * no puede medirse sola (`sizes="auto"`), así que el valor tiene que acertar.
 */
const TAMANOS_TARJETA = '(max-width:720px) 50vw, 270px';

export interface TarjetaProductoProps {
  producto: ProductoResumen;
  /** La primera fila de la portada se carga sin diferir: es el LCP. */
  prioridad?: boolean;
}

export function TarjetaProducto({ producto, prioridad = false }: TarjetaProductoProps) {
  const descuento = porcentajeDescuento(producto.precio, producto.precioAnterior);
  const agotado = producto.estadoStock === 'agotado';
  const enlace = rutas.producto(producto.slug);

  return (
    <article className={agotado ? 'tarjeta agotado' : 'tarjeta'}>
      <div className="tarjeta-img">
        <p className="tarjeta-etqs">
          {descuento !== null ? <span className="etq etq-oferta">-{descuento}%</span> : null}
          {agotado ? <span className="etq etq-critico">Sin stock</span> : null}
        </p>
        <Link to={enlace} tabIndex={-1} aria-hidden="true">
          <FotoProducto
            imagen={producto.imagen}
            nombre={producto.nombre}
            prioridad={prioridad}
            tamanos={TAMANOS_TARJETA}
          />
        </Link>
      </div>

      <div className="tarjeta-cuerpo">
        {producto.marca !== null ? <p className="tarjeta-marca">{producto.marca}</p> : null}

        <h3>
          <Link className="tarjeta-n" to={enlace}>
            {producto.nombre}
          </Link>
        </h3>

        <p className="tarjeta-sku">{producto.sku}</p>
        <Precio precio={producto.precio} precioAnterior={producto.precioAnterior} />
        <EtiquetaPaquete producto={producto} />
        <p className="tarjeta-stock">
          <EtiquetaStock stock={producto.stock} />
        </p>

        {/* Un producto agotado no se puede agregar: deriva a WhatsApp, que es
            donde hoy se resuelve una reposicion (WSP-01). */}
        <p className="tarjeta-acciones">
          {agotado ? (
            <a
              className="btn btn-sm btn-wa"
              href={whatsappProducto(
                producto.nombre,
                producto.sku,
                urlAbsoluta(enlace),
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => registrarClicWhatsapp({ origen: 'ficha', productoSku: producto.sku })}
            >
              <Icono nombre="wa" />
              Consultar
            </a>
          ) : (
            <BotonAgregar producto={producto} tamano="sm" />
          )}
        </p>
      </div>
    </article>
  );
}
